import { Search } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import React, { useState } from 'react';


import { VidalDrugReference, useSearchVidalDrugReferencesQuery } from 'FRONTEND/utils/backendApi/drugReferencesApi';
import useDebounced from 'FRONTEND/utils/hooks/useDebounce';
import i18n from 'FRONTEND/utils/i18n';

type DrugReferencesSearchProps = {
  onMedicineSelected: (medicineReferenceId: string, medicineReferenceType: VidalDrugReference['type']) => void;
  disabled: boolean;
};

const MIN_CHAR_FOR_SEARCH = 3;

/**
 * @param onMedicineSelected Callback function that is called when a medicine reference is selected, receiving the selected medicine reference ID as an argument.
 * @returns A search bar with autocomplete, fed by our internal VIDAL API Proxy.
 */
const DrugReferencesSearch = ({
  onMedicineSelected,
  disabled,
}: DrugReferencesSearchProps) => {
  const [searchString, setSearchString] = useState('');
  const [selectedOption, setSelectedOption] = useState<VidalDrugReference | null>(null);

  // A separate, debounced search string for RTK Query (No built-in debounce feature AFAIK)
  const [debouncedSearch, isDebouncing] = useDebounced<string>(searchString, 300);

  const { data: searchResults, isLoading } = useSearchVidalDrugReferencesQuery(debouncedSearch, {
    skip: debouncedSearch.length < MIN_CHAR_FOR_SEARCH, // Skip the query if the debounced search string is less than 3 characters
  });

  const handleOptionSelected = (_event: React.SyntheticEvent, value: VidalDrugReference | null) => {
    if (value) {
      onMedicineSelected(value.vidal_id, value.type);
      setSearchString('');
      setSelectedOption(null);
    }
  };

  const handleInputChange = (_event: React.ChangeEvent<HTMLInputElement>, _value: string, reason: string) => {
    // Override clear button behaviour.
    if (reason === 'clear') {
      setSearchString('');
    }
  };

  return (
    <Autocomplete
      size='small'
      sx={{
        minWidth: 400,
        '& .MuiInputBase-root': {
          borderRadius: '50vh',
          backgroundColor: (theme) => theme.palette.background.paper,
        },
        '& .MuiOutlinedInput-root': {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.palette.inputFieldGlow.main,
            boxShadow: (theme) => `inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px ${theme.palette.inputFieldGlow.main}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.palette.inputFieldGlow.main,
            boxShadow: (theme) => `inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px ${theme.palette.inputFieldGlow.main}`,
          },
        },

      }}
      onChange={handleOptionSelected} // Not to be confused with input changes, only handles option selection from the dropdown.
      value={selectedOption}
      disabled={disabled}
      inputValue={searchString}
      onInputChange={handleInputChange}
      loading={isLoading || (isDebouncing && searchString.length >= MIN_CHAR_FOR_SEARCH)}
      options={searchString.length >= MIN_CHAR_FOR_SEARCH ? searchResults || [] : []} // Condition ensures results are hidden when user erases input.
      // Results are already filtered server-side (by label or by molecule/ATC classification), so a
      // match's title won't always contain the search string (e.g. searching "apixaban" returns
      // products titled "ELIQUIS ..."). Disable MUI's own text-based re-filtering, which would
      // otherwise hide those results.
      filterOptions={(options) => options}
      loadingText={i18n._('Searching...')}
      getOptionLabel={(option: VidalDrugReference) => option.title}
      // A VMP and a product can share the same Vidal id.
      getOptionKey={(option: VidalDrugReference) => `${option.type}-${option.vidal_id}`}
      noOptionsText={i18n._('No therapeutics found')}
      slotProps={{
        popper: {
          sx: {
            border: 1,
            borderColor: (theme) => theme.palette.inputFieldGlow.main,
            borderRadius: '4px',
          },
        },
        listbox: {
          sx: {
            paddingY: 1,
            '&>:nth-child(even)': {
              backgroundColor: (theme) => theme.palette.background.default,
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={i18n._('Enter at least 3 characters to begin searching')}
          size='small'
          onChange={(event) => setSearchString(event.target.value)}
          disabled={disabled}
          label={i18n._('Search for a therapeutic to add it')}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment /* Preserve the default clear button */}
                  <Search />
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default DrugReferencesSearch;
